import * as THREE from 'three';

/**
 * FACTION ATLAS.
 *
 * `src/chars/materials.js` paints one 1024² albedo per faction and swaps the
 * MATERIAL to change army colours. The crowd cannot afford a material swap —
 * that would double the number of draw calls — so it composites the faction
 * albedos into the four quadrants of a single 2048² texture and offsets the
 * albedo UV per instance instead. Normal + ORM stay shared and untouched,
 * because they use the unmodified `uv` attribute.
 *
 *      +-----------+-----------+
 *      |  sui (0)  |  kai (1)  |
 *      +-----------+-----------+
 *      |  wei (2)  |  sui      |
 *      +-----------+-----------+
 *
 *  vUvA = uv * 0.5 + factionOffset
 *
 * Nothing is downloaded: the source canvases are the ones chars already
 * painted at boot.
 */
export const FACTION_OFFSET = [[0, 0], [0.5, 0], [0, 0.5]];

export function buildFactionAtlas(chars, renderer) {
  const src = chars?.textures?.albedo;
  if (!src) return null;
  const keys = ['sui', 'kai', 'wei'];
  const first = src[keys[0]]?.image;
  if (!first) return null;
  const S = first.width || 1024;
  const cv = document.createElement('canvas');
  cv.width = S * 2; cv.height = S * 2;
  const ctx = cv.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  const at = [[0, 0], [S, 0], [0, S], [S, S]];
  for (let i = 0; i < 4; i++) {
    const img = src[keys[Math.min(i, 2)]]?.image;
    if (img) ctx.drawImage(img, at[i][0], at[i][1], S, S);
  }
  const t = new THREE.CanvasTexture(cv);
  t.flipY = false;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.anisotropy = Math.min(8, renderer?.capabilities?.getMaxAnisotropy?.() ?? 4);
  t.needsUpdate = true;
  t.name = 'crowdFactionAtlas';
  return t;
}
